// Cart lines that /cart/preview refused to price, paired with the reason.
//
// The BE returns those as a 200 with the line missing from `items[]` and its
// explanation in `warnings[]` — a field nothing in this app read until now, so
// the cashier got a cart row at its catalog price above a $0.00 Subtotal/Total
// and a $0.00 Checkout button, then a 400 `cart_contains_invalid_items` on
// tender. See `lib/previewBlocked.core.ts` for the pairing rules.

import { useMemo } from 'react'
import { useCartStore } from '@/stores/cartStore'
import { usePOSStore } from '@/stores/posStore'
import {
  blockedCartLines,
  type BlockedCartResult,
  type PreviewWarningLike,
  type PricedRowLike,
} from '@/lib/previewBlocked.core'

export interface BlockedCartLines extends BlockedCartResult {
  /** Reason by cart-item id, for the row that owns it. */
  byItemId: Map<string, string>
  hasBlocked: boolean
}

const EMPTY: BlockedCartLines = {
  blocked: [],
  notices: [],
  hardRejection: false,
  byItemId: new Map(),
  hasBlocked: false,
}

export function useBlockedCartLines(): BlockedCartLines {
  const cart = usePOSStore((s) => s.cart)
  const isRefundMode = usePOSStore((s) => s.isRefundMode)
  const serverTotals = usePOSStore((s) => s.serverTotals)
  const preview = useCartStore((s) => s.preview)
  const previewStatus = useCartStore((s) => s.previewStatus)
  const lines = useCartStore((s) => s.lines)

  return useMemo(() => {
    // Only a SETTLED preview describes the basket on screen. While 'stale' or
    // 'fetching' the response is a shape behind, so the line the cashier just
    // scanned is legitimately unpriced and would flash red for one debounce;
    // 'error' means we never got an answer about it at all. Refund mode never
    // previews (the BE rejects negative qty), and a serverTotals cart is priced
    // by a committed receipt rather than preview — neither can be judged here.
    if (isRefundMode || serverTotals !== null) return EMPTY
    if (previewStatus !== 'idle' || !preview) return EMPTY

    // `previewStatus` is a property of cartStore.lines -- every mutator flips it
    // to 'stale', and applyPreview only returns it to 'idle' after re-checking
    // the fingerprint of those lines. But the rows we judge live in
    // posStore.cart, which useV2PreviewBridge mirrors into lines from an
    // effect: for the frame between a cart mutation and that effect running,
    // cart holds a line that 'idle' knows nothing about. Judging it there would
    // flash a just-scanned row red. Both sides key on (product_id, lot), so
    // compare that key set and stand down until the mirror has caught up.
    if (!sameLineKeys(cart, lines)) return EMPTY

    const result = blockedCartLines(
      cart.map((item) => ({
        id: item.id,
        product_id: item.product_id,
        lot_number: item.lot_number,
        qty: item.quantity,
        basket_id: item.basket_id ?? null,
      })),
      (preview.items ?? []) as unknown as PricedRowLike[],
      (preview.warnings ?? []) as PreviewWarningLike[],
    )

    return {
      ...result,
      byItemId: new Map(result.blocked.map((b) => [b.id, b.message])),
      hasBlocked: result.blocked.length > 0,
    }
  }, [cart, lines, isRefundMode, serverTotals, preview, previewStatus])
}

/** Multiset equality on the (product_id, lot) key both stores are built around
 *  — the same pair useV2PreviewBridge matches an existing line by. */
function sameLineKeys(
  cart: { product_id: number; lot_number: string | null }[],
  lines: { product_id: number; lot_number: string | null }[],
): boolean {
  if (cart.length !== lines.length) return false
  const counts = new Map<string, number>()
  for (const c of cart) {
    const k = `${c.product_id}|${c.lot_number ?? ''}`
    counts.set(k, (counts.get(k) ?? 0) + 1)
  }
  for (const l of lines) {
    const k = `${l.product_id}|${l.lot_number ?? ''}`
    const n = counts.get(k)
    if (!n) return false
    counts.set(k, n - 1)
  }
  return true
}
